// src/modules/app-views/ticket-events/pages/Document/tabs/DashboardTab/index.jsx
import { useEffect, useState } from 'react';
import { Card, Col, Progress, Row, Statistic, Timeline, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DollarOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { theme } from 'antd';
import { useDocumentContext } from 'components/core/DocumentProvider';
import axiosService from 'services/axiosService';
import { API, apiUrl } from 'constants/Api';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import dayjs from 'dayjs';

const { Text } = Typography;
const { useToken } = theme;


// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TicketEventsDashboardTab = () => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { data } = useDocumentContext();
  const { token } = useToken();

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!data?._id || data._id === 'add') return;
    fetchStats(data._id);
  }, [data?._id]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const fetchStats = async eventId => {
    setLoadingStats(true);
    try {
      const url = apiUrl.build({
        url: API.TICKET_SALES.STATS,
        params: { eventId },
      });
      const [statsData] = await axiosService.get({ url, returnAsObject: true });
      setStats(statsData);
    } catch (_) {
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const sold = data?.soldTickets || 0;
  const total = data?.totalTickets || 0;
  const remaining = total - sold;
  const soldPct = total > 0 ? Math.round((sold / total) * 100) : 0;
  const currentPrice = data?.currentPrice || 0;
  const minimumPrice = data?.minimumPrice || 0;
  const priceDiff = currentPrice - minimumPrice;
  const pricePct = minimumPrice > 0 ? ((priceDiff / minimumPrice) * 100).toFixed(1) : 0;
  const priceHistory = data?.priceHistory || [];
  const totalRevenue = stats?.sales?.totalRevenue || 0;
  const initialForecast = minimumPrice * total;
  const currentForecast = totalRevenue + remaining * currentPrice;

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <div className="container">
      {/* STATUS + KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="stretch">
        <Col xs={24} sm={6} style={{ display: 'flex' }}>
          <Card size="small" style={{ flex: 1 }}>
            <Statistic
              title={translateX('current_price')}
              value={financialService.formatCurrency(currentPrice)}
              prefix={
                priceDiff > 0 ? (
                  <ArrowUpOutlined style={{ color: token.colorSuccess }} />
                ) : priceDiff < 0 ? (
                  <ArrowDownOutlined style={{ color: token.colorError }} />
                ) : null
              }
              suffix={
                pricePct !== 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {pricePct > 0 ? '+' : ''}{pricePct}%
                  </Text>
                ) : null
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={6} style={{ display: 'flex' }}>
          <Card size="small" style={{ flex: 1 }}>
            <Statistic
              title={translateX('tickets')}
              value={`${sold} / ${total}`}
              prefix={<TeamOutlined />}
            />
            <Progress percent={soldPct} size="small" showInfo={false} style={{ marginTop: 4 }} />
          </Card>
        </Col>
        <Col xs={24} sm={6} style={{ display: 'flex' }}>
          <Card size="small" style={{ flex: 1 }}>
            <Statistic
              title={translateX('remaining_tickets')}
              value={remaining}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6} style={{ display: 'flex' }}>
          <Card size="small" style={{ flex: 1 }}>
            <Statistic
              title={translateX('total_revenue')}
              value={financialService.formatCurrency(totalRevenue)}
              prefix={<DollarOutlined />}
              loading={loadingStats}
            />
          </Card>
        </Col>
      </Row>

      {/* INITIAL FORECAST + CURRENT FORECAST */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="stretch">
        <Col xs={24} sm={12} style={{ display: 'flex' }}>
          <Card size="small" style={{ flex: 1 }}>
            <Statistic
              title={translateX('initial_forecast')}
              value={financialService.formatCurrency(initialForecast)}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} style={{ display: 'flex' }}>
          <Card size="small" style={{ flex: 1 }}>
            <Statistic
              title={translateX('current_forecast')}
              value={financialService.formatCurrency(currentForecast)}
              prefix={<DollarOutlined />}
              loading={loadingStats}
            />
          </Card>
        </Col>
      </Row>

      {/* PRICE HISTORY */}
      {priceHistory.length > 0 && (
        <Card
          title={<span className="text-muted">{translateX('price_history')}</span>}
          size="small"
        >
          <Timeline
            items={buildTimelineItems(priceHistory).map((entry, idx) => ({
              key: idx,
              color: entry._dotColor,
              content: (
                <div>
                  <Text strong>{financialService.formatCurrency(entry.price)}</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>
                    score: {entry.score?.toFixed(2)}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {translatePricingReason(entry.reason)}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {entry.calculatedAt ? dayjs(entry.calculatedAt).format('DD/MM/YY HH:mm') : '-'}
                  </Text>
                </div>
              ),
            }))}
          />
        </Card>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TicketEventsDashboardTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Maps pricing reason phrases (English, comma-separated) to translated labels.
// The reason field is built by pricingEngineService.buildReason() as a comma-separated
// list of phrases like "high demand, ample stock, slow sales velocity".
// Reverses price history (most recent first), slices to 10 entries,
// and annotates each entry with a _dotColor based on price change vs previous entry.
function buildTimelineItems(priceHistory) {
  const reversed = [...priceHistory].reverse().slice(0, 10);
  return reversed.map((entry, idx) => {
    const prevEntry = reversed[idx + 1]; // previous in original order = next in reversed array
    let dotColor = 'gray';
    if (prevEntry) {
      if (entry.price > prevEntry.price) dotColor = 'green';
      else if (entry.price < prevEntry.price) dotColor = 'red';
    } else {
      // Last (oldest) entry: compare against itself — no change
      dotColor = 'gray';
    }
    return { ...entry, _dotColor: dotColor };
  });
}

const REASON_KEY_MAP = {
  'high demand': 'price_history_high_demand',
  'low demand': 'price_history_low_demand',
  'normal demand': 'price_history_normal_demand',
  'ample stock': 'price_history_ample_stock',
  'very scarce stock': 'price_history_very_scarce_stock',
  'moderate scarcity': 'price_history_moderate_scarcity',
  'fast sales velocity': 'price_history_fast_sales_velocity',
  'slow sales velocity': 'price_history_slow_sales_velocity',
  'critical window: oversupply': 'price_history_critical_window_oversupply',
  'critical window: scarce stock': 'price_history_critical_window_scarce',
};

function translatePricingReason(reason) {
  if (!reason) return '-';
  return reason
    .split(', ')
    .map(part => {
      const key = REASON_KEY_MAP[part.trim().toLowerCase()];
      return key ? translateX(key) : part;
    })
    .join(', ');
}
